import styled from 'styled-components';

interface WrapperProps {
  $scrollLocked: boolean;
}

interface ContentProps {
  $footerVisible: boolean;
}

const ROUNDED_FONT =
  'var(--ed-font-rounded, ui-rounded, "SF Pro Rounded", Nunito, "SF Pro Text", -apple-system, BlinkMacSystemFont, "Roboto", sans-serif)';

export const Wrapper = styled.section<WrapperProps>`
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  overflow-y: ${({ $scrollLocked }) => ($scrollLocked ? 'hidden' : 'auto')};
  overscroll-behavior: contain;
  background: #F8F3EB;
`;

export const Content = styled.div<ContentProps>`
  position: relative;
  width: 100%;
  max-width: 450px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: calc(env(safe-area-inset-top) + 24px) 20px
    ${({ $footerVisible }) =>
      $footerVisible ? 'calc(env(safe-area-inset-bottom) + 240px)' : 'calc(env(safe-area-inset-bottom) + 32px)'};
  background: #F8F3EB;
`;

export const ClosePageButton = styled.button`
  position: absolute;
  top: calc(env(safe-area-inset-top) + 18px);
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #beb7ad;
  line-height: 1;
  background: transparent;
  cursor: pointer;
  z-index: 10;

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
  }
`;

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border-radius: 24px;
  background: #e44a08;
  box-shadow: inset 1px -3px 16px 0 rgba(255, 255, 255, 0.6);
  color: #fffbf2;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: -0.036px;
  text-transform: uppercase;

  svg {
    width: 10px;
    height: 10px;
    min-width: 10px;
    min-height: 10px;
  }
`;

export const Title = styled.h2`
  margin: 0;
  color: #221e1a;
  font-family: ${ROUNDED_FONT};
  font-size: 28px;
  font-weight: 800;
  line-height: 36px;
  letter-spacing: -0.003px;
  text-align: center;

  span {
    color: #e44a08;
  }
`;

export const Subtitle = styled.p`
  max-width: 315px;
  margin: 0 auto;
  color: #221e1a;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.045px;
  text-align: center;
`;

export const ProductsList = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const ProductCard = styled.article`
  width: 100%;
  border-radius: 32px;
  padding: 20px;
  background: #fcfbf9;
  box-shadow: 0 12px 32px -8px rgba(42, 61, 82, 0.08);
  display: flex;
  flex-direction: column;
`;

export const ProductTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ProductImage = styled.img`
  width: 90px;
  height: 100px;
  min-width: 90px;
  border-radius: 16px;
  object-fit: cover;
`;

export const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ProductTitle = styled.h3`
  margin: 0;
  color: #221e1a;
  font-family: ${ROUNDED_FONT};
  font-size: 19px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.076px;
`;

export const ProductMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #a19d97;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.036px;

  svg {
    width: 11px;
    height: 10px;
    min-width: 11px;
  }
`;

export const ProductRating = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const ProductDescription = styled.p`
  margin: 0;
  color: #221e1a;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.039px;
`;

export const ProductFooter = styled.div`
  border-top: 2px dashed var(--ed-border, rgba(110, 132, 156, 0.1));
  padding-top: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const PriceBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const OldPrice = styled.span`
  color: #221e1a;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.036px;
  text-decoration: line-through;
`;

export const NewPrice = styled.span`
  color: #e44a08;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.045px;
`;

export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 20px 11px 16px;
  border-radius: 999px;
  background: #f0930c;
  color: #fffbf2;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.042px;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
  }
`;

export const QuantityControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 3px;
  border: 1.5px solid var(--ed-border, #ecebe6);
  border-radius: 99px;
`;

export const QuantityMinus = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: transparent;
  color: #221e1a;
  cursor: pointer;

  &:active {
    opacity: 0.6;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const QuantityValue = styled.span`
  min-width: 24px;
  text-align: center;
  color: #221e1a;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.045px;
`;

export const QuantityPlus = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #f0930c;
  color: #fffbf2;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Footer = styled.div`
  position: fixed;
  left: 50%;
  bottom: 0;
  z-index: 25;
  width: 100%;
  max-width: 450px;
  box-sizing: border-box;
  padding: 16px 20px calc(env(safe-area-inset-bottom) + 16px);
  padding-top: 8px;
  border-radius: 32px 32px 0 0;
  border-top: 1.5px solid var(--Interactive-Elements-Cards-Border-BorderInactive, #F2E8DA);
  background: #F7F0EA;
  transform: translateX(-50%);
`;

export const SummaryRow = styled.div`
  padding: 0 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #221e1a;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.045px;

  & + & {
    margin-top: 4px;
  }
`;

export const TotalRow = styled.div`
  margin-top: 4px;
  border-radius: 12px;
  background: rgba(190, 226, 188, 0.4);
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    color: #221e1a;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.045px;
  }

  strong {
    color: #29a073;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.045px;
  }
`;

export const PayButton = styled.button`
  margin-top: 16px;
  padding: 16px;
  width: 100%;
  min-height: 60px;
  border-radius: 48px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0.088px;
  font-weight: 700;
  color: #fff;
  background: var(--ed-brand, #ae66f6);
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.7;
    pointer-events: none;
  }
`;

export const PayButtonText = styled.span`
  grid-column: 2;
  justify-self: center;
`;

export const Spinner = styled.span`
  grid-column: 3;
  justify-self: start;
  width: 16px;
  height: 16px;
  margin-left: 8px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: supplements-spin 0.8s linear infinite;

  @keyframes supplements-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  touch-action: none;
  background: rgba(35, 30, 25, 0.54);
`;

export const ModalCard = styled.div`
  position: relative;
  max-width: 450px;
  width: 100%;
  border-radius: 24px;
  background: #fffbf2;
  padding: 32px 20px 30px;
`;

export const ModalTitle = styled.h3`
  margin: 0;
  padding-right: 48px;
  color: #221e1a;
  font-family: ${ROUNDED_FONT};
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
  letter-spacing: -0.003px;
`;

export const ModalCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 12px;
  background: #f2ede1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ModalText = styled.p`
  margin: 10px 0 0;
  color: #221e1a;
  font-size: 17px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.051px;
`;

export const EmailField = styled.input`
  margin-top: 22px;
  width: 100%;
  min-height: 60px;
  border-radius: 40px;
  background: #f2ede1;
  padding: 12px 12px 12px 24px;
  color: #221e1a;
  text-align: left;
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.068px;
`;

export const PrivacyRow = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  svg {
    width: 17px;
    min-width: 17px;
    height: 18px;
  }
`;

export const PrivacyText = styled.p`
  margin: 0;
  color: #7a7670;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.036px;

  a {
    text-decoration: underline;
  }
`;

export const SubscribeButton = styled.button`
  margin-top: 20px;
  padding: 16px;
  width: 100%;
  min-height: 60px;
  border-radius: 48px;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0.088px;
  font-weight: 700;
  color: #fff;
  background: var(--ed-brand, #ae66f6);
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
`;
